import { AnimatePresence, motion } from 'framer-motion';
import {
  Award,
  Calendar,
  ChevronDown,
  Clock,
  FileText,
  History,
  LayoutDashboard,
  LogOut,
  Menu,
  Monitor,
  Moon,
  Sun,
  User,
  Users,
  Wallet,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import neolabShieldMinimal from '@/assets/images/neolab_shield_minimal.svg';
import { useAuth } from '@/hooks';
import { useThemeStore } from '@/store/themeStore';
import { cn } from '@/utils/cn';

const staffNavItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/attendance', label: 'Attendance', icon: Clock },
  { path: '/roaster', label: 'Roaster', icon: Calendar },
  { path: '/deductions', label: 'Deductions', icon: Wallet },
  { path: '/pay-checks', label: 'Pay Checks', icon: History },
  { path: '/profile', label: 'Profile', icon: User },
];

const adminNavItems = [
  { path: '/dashboard', label: 'User Dashboard', icon: User },
  { path: '/admin/dashboard', label: 'Admin Dashboard', icon: LayoutDashboard },
  { path: '/admin/staff', label: 'Staff Management', icon: Users },
  { path: '/admin/attendance', label: 'Attendance', icon: Calendar },
  { path: '/admin/roaster', label: 'Roaster', icon: Calendar },
  { path: '/admin/punctuality', label: 'Punctuality', icon: Award },
  { path: '/admin/payments', label: 'Payments', icon: Wallet },
  { path: '/admin/reports', label: 'Reports', icon: FileText },
];

export function AppLayout() {
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const { theme, setTheme, resolvedTheme } = useThemeStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [themeMenuOpen, setThemeMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = isAdmin ? adminNavItems : staffNavItems;

  const handleLogout = async () => {
    await logout();
  };

  const ThemeIcon = resolvedTheme === 'dark' ? Moon : Sun;

  return (
    <div className="min-h-screen bg-white dark:bg-neolab-deep-green transition-colors duration-500">
      <div id="modal-root" className="fixed inset-0 z-[200] pointer-events-none" />
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-neolab-deep-green/60 backdrop-blur-md z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') setSidebarOpen(false);
            }}
            role="button"
            tabIndex={0}
            aria-label="Close sidebar"
          />
        )}
      </AnimatePresence>

      <aside
        className={cn(
          'fixed top-0 left-0 z-50 h-full w-72 border-r border-neolab-green/5 dark:border-white/10 transform transition-transform duration-300 ease-in-out lg:translate-x-0',
          'bg-white dark:bg-neolab-forest shadow-[20px_0_40px_rgba(0,0,0,0.02)] lg:shadow-none',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="h-20 flex items-center px-8">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <motion.div
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.2 }}
                className="w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center shadow-lg shadow-green-500/20"
              >
                <img
                  src={neolabShieldMinimal}
                  alt="Neolab Shield Minimal"
                  className="w-full h-full object-contain"
                />
              </motion.div>
            </div>
            <div>
              <span className="font-black text-xl tracking-tighter text-slate-900 dark:text-white">
                NEO
              </span>
              <span className="font-black text-xl tracking-tighter text-neolab-green">lab</span>
            </div>
          </div>
        </div>

        <nav className="px-4 py-4 space-y-2">
          <p className="px-4 text-xs font-black text-neolab-forest dark:text-neolab-green uppercase tracking-[0.3em] mb-4">
            System Management
          </p>
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  'flex items-center gap-3 px-5 py-3.5 rounded-2xl font-black transition-all duration-300 group relative text-xs uppercase tracking-widest',
                  isActive
                    ? 'bg-gradient-brand text-white shadow-xl shadow-green-500/20'
                    : 'text-slate-400 hover:bg-neolab-green/5 dark:hover:bg-white/5 hover:text-neolab-green',
                )}
              >
                <Icon
                  className={cn(
                    'w-5 h-5 transition-transform duration-300 group-hover:scale-110',
                    isActive ? 'text-white' : 'text-slate-300 group-hover:text-neolab-green',
                  )}
                />
                <span className="font-black">{item.label}</span>
                {isActive && (
                  <motion.div
                    layoutId="sidebar-active-indicator"
                    className="absolute right-5 w-1.5 h-1.5 bg-white rounded-full shadow-[0_0_8px_rgba(255,255,255,0.8)]"
                  />
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="p-4 rounded-[1.5rem] bg-neolab-green/[0.02] dark:bg-white/5 border-2 border-neolab-green/[0.05] dark:border-white/10 group hover:border-neolab-green/30 transition-all duration-300 backdrop-blur-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-gradient-brand flex items-center justify-center text-white font-black text-base shadow-lg shadow-green-500/20">
                {user?.profile?.firstName?.[0]}
                {user?.profile?.lastName?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-black text-slate-900 dark:text-white truncate">
                  {user?.profile?.firstName}
                </p>
                <p className="text-xs font-black text-neolab-green uppercase tracking-[0.15em]">
                  {user?.role === 'super_admin'
                    ? 'Super Administrator'
                    : user?.role === 'admin'
                      ? 'Administrator'
                      : 'Staff Member'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-white dark:bg-white/5 text-rose-500 hover:bg-rose-500 hover:text-white font-black text-xs uppercase tracking-widest transition-all duration-300 shadow-sm border border-slate-100 dark:border-white/5"
            >
              <LogOut className="w-4 h-4" />
              <span>SIGN OUT</span>
            </button>
          </div>
        </div>
      </aside>

      <div className="lg:pl-72 flex flex-col min-h-screen">
        <header
          className={cn(
            'sticky top-0 z-30 h-20 transition-all duration-500 px-6 lg:px-10 flex items-center justify-between',
            scrolled
              ? 'bg-white/80 dark:bg-neolab-deep-green/80 backdrop-blur-2xl border-b border-slate-100 dark:border-white/10 shadow-sm'
              : 'bg-transparent',
          )}
        >
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-3 rounded-xl bg-white dark:bg-white/5 border-2 border-slate-100 dark:border-white/10 shadow-sm text-neolab-green backdrop-blur-xl"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center gap-3 lg:gap-6">
            <div className="relative">
              <button
                type="button"
                onClick={() => setThemeMenuOpen(!themeMenuOpen)}
                className="flex items-center gap-2.5 px-4 py-2.5 rounded-lg bg-gradient-brand text-white shadow-xl shadow-green-500/20 hover:shadow-green-500/40 transition-all border-b-2 border-neolab-forest/50"
              >
                <ThemeIcon className="w-4 h-4" />
                <span className="hidden lg:block text-[10px] font-black uppercase tracking-[0.15em]">
                  {theme}
                </span>
                <ChevronDown
                  className={cn(
                    'w-3.5 h-3.5 transition-transform duration-300',
                    themeMenuOpen && 'rotate-180',
                  )}
                />
              </button>

              <AnimatePresence>
                {themeMenuOpen && (
                  <>
                    <button
                      type="button"
                      className="fixed inset-0 z-40 bg-transparent cursor-default w-full h-full"
                      onClick={() => setThemeMenuOpen(false)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape' || e.key === 'Enter') setThemeMenuOpen(false);
                      }}
                      aria-label="Close theme menu"
                    />
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute right-0 top-full mt-3 w-48 bg-white dark:bg-neolab-deep-green rounded-xl shadow-[0_20px_40px_rgba(0,0,0,0.15)] border-2 border-slate-50 dark:border-white/10 p-2.5 z-50 overflow-hidden backdrop-blur-2xl"
                    >
                      {[
                        {
                          id: 'light',
                          icon: Sun,
                          label: 'Light',
                          color: 'text-amber-500',
                          bg: 'bg-amber-500/10',
                        },
                        {
                          id: 'dark',
                          icon: Moon,
                          label: 'Dark',
                          color: 'text-neolab-green',
                          bg: 'bg-neolab-green/10',
                        },
                        {
                          id: 'system',
                          icon: Monitor,
                          label: 'System',
                          color: 'text-slate-500',
                          bg: 'bg-slate-500/10',
                        },
                      ].map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => {
                            setTheme(t.id as 'light' | 'dark' | 'system');
                            setThemeMenuOpen(false);
                          }}
                          className={cn(
                            'w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[10px] font-black tracking-[0.15em] transition-all mb-0.5 last:mb-0',
                            theme === t.id
                              ? 'bg-gradient-brand text-white shadow-lg'
                              : 'text-slate-500 dark:text-white/40 hover:bg-slate-50 dark:hover:bg-white/5',
                          )}
                        >
                          <div
                            className={cn('p-1 rounded-lg', theme === t.id ? 'bg-white/20' : t.bg)}
                          >
                            <t.icon
                              className={cn('w-3.5 h-3.5', theme === t.id ? 'text-white' : t.color)}
                            />
                          </div>
                          {t.label}
                        </button>
                      ))}
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-6 max-w-[1500px] mx-auto w-full relative">
          <div className="absolute top-40 right-10 w-72 h-72 bg-neolab-green/5 rounded-full blur-[80px] pointer-events-none" />
          <div className="absolute bottom-40 left-10 w-64 h-64 bg-accent-rose/5 rounded-full blur-[60px] pointer-events-none" />

          <div className="relative z-10">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

export default AppLayout;
